// ===============================================================
// ✅ BottomNav.js — LIVORA FINAL (+ Beauty icon)
// ✅ theme-aligned + clean structure
// ===============================================================

import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  FaHome,
  FaComments,
  FaCamera,
  FaVideo,
  FaUser,
  FaBell,
} from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { collection, query, where, onSnapshot } from "firebase/firestore";
import { db, auth } from "../firebase";
import { openSnapNativeCamera } from "../plugins/NativeFilterLab";
import { countSupportedUnread } from "../notifications/notificationTypes";
import {
  isNativeNotificationCenterAvailable,
  openNativeNotificationCenter,
} from "../plugins/NativeNotificationCenter";
import { useGenderTheme } from "../context/GenderThemeContext";
import {
  useIsNativeBottomNavShellActive,
  useNativeShellUnreadCount,
  isNativeShellUnreadOwnerActive,
} from "./NativeShellController";
import "./BottomNav.css";

function BottomNav({ active = "" }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isNativeShellActive = useIsNativeBottomNavShellActive();
  const [ownUnreadCount, setUnreadCount] = useState(0);
  const sharedUnreadCount = useNativeShellUnreadCount();
  // NativeShellController becomes the sole unread-count listener owner
  // the instant the native shell system is even potentially in play
  // (flag on + plugin present), not only once it has fully taken over -
  // see isNativeShellUnreadOwnerActive's own comment for why that exact
  // switch point avoids a brief dual-listener window. On web/browser/iOS
  // this is always false and ownUnreadCount (this component's own
  // listener, below, unchanged) remains authoritative.
  const unreadCount = isNativeShellUnreadOwnerActive() ? sharedUnreadCount : ownUnreadCount;
  // Canonical, closed-set "male"/"female"/"default" - see
  // GenderThemeContext.js for why this must never be read back off
  // document.documentElement's data-theme attribute (that attribute can
  // also legitimately hold "dark"/"light" from the unrelated Appearance
  // settings toggle).
  const genderTheme = useGenderTheme();
  const cameraNavBusyRef = useRef(false);

  useEffect(() => {
    if (isNativeShellUnreadOwnerActive()) {
      // NativeShellController already owns the one listener in this
      // mode - see above.
      return undefined;
    }

    const currentUser = auth.currentUser;

    if (!currentUser?.uid) {
      setUnreadCount(0);
      return undefined;
    }

    const q = query(
      collection(db, "notifications"),
      where("uid", "==", currentUser.uid),
      where("read", "==", false)
    );

    const unsub = onSnapshot(
      q,
      (snap) => {
        // Firestore query is unchanged (uid + read==false only) to avoid
        // requiring a new composite index; the type filter is applied
        // client-side here so the badge only ever counts notifications the
        // Notification Center can actually display and let the user clear -
        // this is what closes the "badge stuck after tapping the bell" gap
        // for message/call notifications (see notificationTypes.js).
        setUnreadCount(countSupportedUnread(snap.docs));
      },
      (err) => {
        console.error("notifications badge error:", err);
        setUnreadCount(0);
      }
    );

    return () => unsub();
  }, []);

  // Android: open the real native production Notification Center.
  // Web/non-native: keep the existing /notifications React route.
  const handleBellPress = useCallback(async () => {
    if (isNativeNotificationCenterAvailable()) {
      const result = await openNativeNotificationCenter(genderTheme);
      if (result?.opened) {
        return;
      }
      // Fall through to the web route if the native screen genuinely
      // couldn't open, so the bell never becomes a dead button.
    }
    navigate("/notifications");
  }, [navigate, genderTheme]);

  const handleCameraNavPress = useCallback(async () => {
    if (cameraNavBusyRef.current) {
      return;
    }
    cameraNavBusyRef.current = true;

    try {
      console.log("[BOTTOM_NAV_CAMERA] open_snap_native_start");
      const result = await openSnapNativeCamera();

      if (result?.opened === true) {
        console.log("[BOTTOM_NAV_CAMERA] open_snap_native_success");
      } else {
        console.log(
          "[BOTTOM_NAV_CAMERA] open_snap_native_fallback",
          result?.reason ?? "unknown"
        );
        navigate("/snap-camera");
      }
    } finally {
      cameraNavBusyRef.current = false;
    }
  }, [navigate]);

  if (isNativeShellActive) {
    // The native bar is handling bottom-navigation for this session -
    // never render both simultaneously. All hooks above still ran
    // unconditionally (rules of hooks), only the render output changes.
    return null;
  }

  return (
    <nav className="bottom-nav" aria-label={t("bottomNav.aria_label", { defaultValue: "Bottom Navigation" })}>
      {/* 🏠 Home */}
      <button
        type="button"
        className={`nav-item ${active === "home" ? "active" : ""}`}
        onClick={() => navigate("/home")}
        aria-label={t("bottomNav.home", { defaultValue: "Home" })}
      >
        <FaHome className="nav-icon" />
      </button>

      {/* 💬 Messages */}
      <button
        type="button"
        className={`nav-item ${active === "messages" ? "active" : ""}`}
        onClick={() => navigate("/messages")}
        aria-label={t("bottomNav.messages", { defaultValue: "Messages" })}
      >
        <FaComments className="nav-icon" />
      </button>

      {/* 📷 Camera (Snap Native, falls back to Snap Web) */}
      <button
        type="button"
        className={`nav-item nav-item-beauty ${
          active === "snap-camera" ? "active" : ""
        }`}
        onClick={handleCameraNavPress}
        aria-label={t("bottomNav.camera", { defaultValue: "Camera" })}
      >
        <FaCamera className="nav-icon" />
      </button>

      {/* 🎥 Match Radar */}
      <button
        type="button"
        className={`nav-item ${active === "live" ? "active" : ""}`}
        onClick={() => navigate("/match-radar")}
        aria-label={t("bottomNav.match_radar", { defaultValue: "Match Radar" })}
      >
        <FaVideo className="nav-icon" />
      </button>

      {/* 🔔 Notifications */}
      <button
        type="button"
        className={`nav-item ${active === "notifications" ? "active" : ""}`}
        onClick={handleBellPress}
        aria-label={t("bottomNav.notifications", { defaultValue: "Notifications" })}
      >
        <span className="nav-bell-wrapper">
          <FaBell className="nav-icon" />
          {unreadCount > 0 && (
            <span className="nav-badge">
              {unreadCount > 9 ? "9+" : unreadCount}
            </span>
          )}
        </span>
      </button>

      {/* 👤 Profile */}
      <button
        type="button"
        className={`nav-item ${active === "profile" ? "active" : ""}`}
        onClick={() => navigate("/my-profile")}
        aria-label={t("bottomNav.profile", { defaultValue: "Profile" })}
      >
        <FaUser className="nav-icon" />
      </button>
    </nav>
  );
}

export default BottomNav;
